<script lang="ts" setup>
import {Button, Space} from 'ant-design-vue';
import {computed, defineProps, ref, watch, watchEffect, isVNode, createTextVNode, onMounted} from 'vue';
import {IconCrossBig} from '../icons';
import config from './release-3.2';
import {getLanguage} from '../../core/lang';
import {trackEvent} from '../app/analytics';

const props = defineProps<{
    context: string;
    disabled: boolean;
}>();

watch(
    () => props.disabled,
    () => {
        open.value = !props.disabled;
    }
);

const storageKey = `mx-tour-${config.version}-${props.context}`;
const open = ref(!props.disabled);
const isComponent = computed(
    () =>
        !(config.contexts[props.context] instanceof Array) &&
        (config.contexts[props.context] as TourComponent)?.component
);
const steps = computed(() => {
    return !isComponent.value ? props.context && (config.contexts[props.context] as MXTourStep[]) : [];
});
const highlightStyle = ref({});
const style = ref({});
const language = getLanguage();
const stepsCount = computed(() => (!isComponent.value && steps.value?.filter((s) => s.counter).length) || 0);
const stepsLeft = computed(() => steps.value?.slice(currentStep.value).filter((s) => s.counter).length || 0);

function close() {
    open.value = false;
    currentStep.value = 0;
}

function next() {
    if (currentStep.value + 1 >= steps.value.length) {
        close();
        return;
    } else {
        if (steps.value[currentStep.value].after) {
            steps.value[currentStep.value].after();
        }
        currentStep.value++;
    }
}

function prev() {
    if (currentStep.value == 0) {
        return;
    }
    currentStep.value--;
}

function finish() {
    if (steps.value[currentStep.value].after) {
        steps.value[currentStep.value].after();
    }
    localStorage.setItem(storageKey, '1');
    trackEvent('screen', `${props.context}-tour-finish`, 'close');
    close();
}

function skip() {
    if (steps.value[currentStep.value].after) {
        steps.value[currentStep.value].after();
    }
    localStorage.setItem(storageKey, '1');
    trackEvent('screen', `${props.context}-tour-skip`, 'open');
    close();
}

function isSkipped() {
    return localStorage.getItem(storageKey) === '1';
}

const currentStep = ref(0);
const step = ref(null);

async function waitForReady(s): Promise<void> {
    return new Promise((resolve) => {
        if (!s.target()) {
            let counts = 0;
            const wait = setInterval(() => {
                if (counts > 300) {
                    clearInterval(wait);
                    throw 'Unable to find target element to start the tour';
                }
                if (s.target()) {
                    clearInterval(wait);
                    resolve();
                }
                counts++;
            }, 250);
        } else {
            resolve();
        }
    });
}

watchEffect(async () => {
    if (!isComponent.value && steps.value && currentStep.value != null) {
        const s = steps.value[currentStep.value];
        if (isSkipped()) {
            close();
            return;
        }
        await waitForReady(s);
        if (s.before) {
            await s.before();
        }
        step.value = s;
        applyStyles();
    }
});

watch(
    () => props.disabled,
    () => {
        if (!props.disabled) {
            //
            trackEvent('screen', `${props.context}-tour-start`, 'open');
        }
    },
    {
        immediate: true
    }
);

function applyStyles() {
    highlightStyle.value = {};
    style.value = {};
    if (step.value) {
        const padding = step.value.targetPadding || [4, 4, 4, 4];
        const offset = step.value.offset || [8, 8];
        const width = getForLanguage(step.value.width);
        const placement = step.value.placement || 'absolute-bottom-right';
        const s: any = Object.assign({width: `${width}px`}, step.value.style);
        if (placement.includes('absolute')) {
            if (placement === 'absolute-center') {
                s.top = '50%';
                s.left = '50%';
                s.transform = 'translate(-50%, -50%)';
            }
            if (placement.includes('top')) {
                s.top = `${offset[1]}px`;
            } else if (placement.includes('bottom')) {
                s.bottom = `${offset[1]}px`;
            }
            if (placement.includes('left')) {
                s.left = `${offset[0]}px`;
            } else if (placement.includes('right')) {
                s.right = `${offset[0]}px`;
            } else if (placement.includes('center')) {
                s.left = '50%';
                s.transform = 'translateX(-50%)';
            }
            if (step.value.mask) {
                if (step.value.mask !== true)
                    highlightStyle.value = {
                        boxShadow: `0px 0px 0 10000px ${step.value.mask}`,
                        border: 'none'
                    };
            } else {
                highlightStyle.value = {
                    display: 'none'
                };
            }
        } else {
            const target = step.value.target();
            const rect = target.getBoundingClientRect();
            if (placement.includes('top')) {
                s.top = `${rect.top - offset[1]}px`;
                // s.transform = '';
            } else if (placement.includes('bottom')) {
                s.top = `${rect.bottom + offset[1]}px`;
            }
            if (placement.includes('left')) {
                s.left = `${rect.left + offset[0]}px`;
            } else if (placement.includes('right')) {
                s.left = `${rect.right - width - offset[0]}px`;
            } else if (placement.includes('center')) {
                s.left = `${rect.left + rect.width / 2 + offset[0]}px`;
            }

            if (placement.includes('right-top')) {
                s.top = `${rect.top + offset[1]}px`;
                s.left = `${rect.right + offset[0]}px`;
                s.transform = 'translateY(0)';
            }

            if (step.value.mask) {
                let {top, left, width, height} = rect;
                top -= padding[0];
                left -= padding[3];
                width += padding[1] + padding[3];
                height += padding[0] + padding[2];
                highlightStyle.value = {
                    top: `${top}px`,
                    left: `${left}px`,
                    width: `${width}px`,
                    height: `${height}px`,
                    display: 'block'
                };
            } else {
                highlightStyle.value = {
                    display: 'none'
                };
            }
        }

        style.value = s;
    }
}

function getForLanguage(o) {
    return o[language] || o.en || '';
}

function asVNode(o) {
    if (isVNode(o)) {
        return o;
    }
    return createTextVNode(o);
}

// update style on window resize if there is a component
const updateStyle = () => {
    if (isComponent.value) {
        style.value = (config.contexts[props.context] as TourComponent).style();
    }
};

onMounted(() => {
    updateStyle();
    window.addEventListener('resize', updateStyle);
});
</script>
<template>
    <Teleport to="body">
        <div class="absolute overflow-hidden" v-if="isComponent" :style="style">
            <component :is="(config.contexts[context] as TourComponent).component" />
        </div>
        <div
            v-else-if="open && step"
            class="mx-tour"
            :class="{mask: step.lock !== false, [step.size || 'medium']: true, [step.theme]: true}"
        >
            <div class="mx-tour-highlight" :style="highlightStyle"></div>
            <div class="mx-tour-window" :style="style" :class="{[step.placement]: true}">
                <div class="arrow"></div>
                <IconCrossBig v-if="step.closable !== false" @click="close" class="mx-tour-window-close" />
                <div class="mx-tour-header">
                    <component :is="asVNode(getForLanguage(step.title))" />
                </div>
                <div class="mx-tour-body">
                    <component :is="asVNode(getForLanguage(step.description))" />
                </div>
                <div class="mx-tour-footer">
                    <div class="mx-tour-counter" v-if="step.counter">
                        {{ stepsCount - stepsLeft + 1 }}/{{ stepsCount }}
                    </div>
                    <Space :size="8">
                        <Button
                            v-if="step.prevButton"
                            :type="step.prevButton?.type"
                            :size="step.size || 'medium'"
                            @click="
                                () =>
                                    step.prevButton?.action
                                        ? step.prevButton?.action({prev, next, skip, close, finish})
                                        : prev()
                            "
                            v-bind="step.prevButton?.buttonParams || {}"
                        >
                            <component :is="asVNode(getForLanguage(step.prevButton.text))" />
                        </Button>
                        <Button
                            v-if="step.centerButton"
                            :type="step.centerButton?.type"
                            :size="step.size || 'medium'"
                            @click="
                                () =>
                                    step.centerButton?.action &&
                                    step.centerButton?.action({prev, next, skip, close, finish})
                            "
                            v-bind="step.centerButton?.buttonParams || {}"
                        >
                            <component :is="asVNode(getForLanguage(step.centerButton.text))" />
                        </Button>
                        <Button
                            v-if="step.nextButton"
                            :type="step.nextButton?.type"
                            :size="step.size || 'medium'"
                            @click="
                                () =>
                                    step.nextButton?.action
                                        ? step.nextButton?.action({prev, next, skip, close, finish})
                                        : next()
                            "
                            v-bind="step.nextButton?.buttonParams || {}"
                        >
                            <component :is="asVNode(getForLanguage(step.nextButton.text))" />
                        </Button>
                    </Space>
                </div>
            </div>
        </div>
    </Teleport>
</template>
<style lang="less" scoped>
.mx-tour {
    transition: all 300ms ease-in;

    &.mask {
        position: absolute;
        inset: 0;
        z-index: 9998;
    }

    &.medium {
        font-size: 14px;
        .mx-tour-header {
            font-size: 16px;
        }
        .mx-tour-counter {
            font-size: 12px;
        }
    }
    &.large {
        font-size: 16px;
        .mx-tour-window {
            &-close {
                top: 20px;
                right: 16px;
                height: 16px;
            }
        }
        .mx-tour-header {
            font-size: 24px;
            padding: 8px 16px;
        }
        .mx-tour-body {
            padding: 0 16px;
        }
        .mx-tour-footer {
            padding: 16px;
        }
        .mx-tour-counter {
            font-size: 14px;
        }
    }

    &.light {
        color: #000;
        .mx-tour-window {
            background-color: #fff;
        }
        .arrow {
            background-color: #fff;
        }
    }
    &.dark {
        color: #fff;
        .mx-tour-window {
            background-color: #1b1c41;
        }
        .arrow {
            background-color: #1b1c41;
        }
        .mx-tour-counter {
            color: #fff;
        }
        .ant-btn-ghost {
            color: #fff;
            background-color: #3d3e5d;
            &:hover {
                border: 1px solid #a4a4b2;
            }
        }
    }

    &-window {
        position: absolute;
        z-index: 9999;
        padding: 4px;
        border-radius: 8px;
        transition:
            left,
            top,
            right,
            bottom 300ms ease-out;
        &-close {
            position: absolute;
            cursor: pointer;
            top: 16px;
            right: 12px;
            height: 12px;
        }
        .arrow {
            width: 14px;
            height: 14px;
            transform: rotate(45deg);
            position: absolute;
            border-radius: 2px;
            display: none;
        }
        &.bottom-center {
            transform: translateX(-50%);
            .arrow {
                display: block;
                top: -6px;
                left: 50%;
            }
        }
        &.bottom-left {
            .arrow {
                display: block;
                top: -6px;
                left: 10%;
            }
        }
        &.bottom-right {
            .arrow {
                display: block;
                top: -6px;
                right: 10%;
            }
        }
        &.top-center {
            transform: translate(-50%, -100%);
            .arrow {
                display: block;
                bottom: -6px;
                left: 50%;
            }
        }
        &.top-left {
            transform: translateY(-100%);
            .arrow {
                display: block;
                bottom: -6px;
                left: 10%;
            }
        }
        &.top-right {
            transform: translateY(-100%);
            .arrow {
                display: block;
                bottom: -6px;
                right: 10%;
            }
        }
        &.right-top {
            transform: translateY(-100%);
            .arrow {
                display: block;
                top: 15%;
                left: -6px;
            }
        }
    }
    &-header {
        padding: 8px;
        font-size: 16px;
        font-weight: 600;
        padding-right: 24px;
    }
    &-body {
        padding: 0 8px;
    }
    &-footer {
        padding: 8px;
        text-align: right;
        .ant-btn-link {
            color: #a4a4b2;
            &:hover {
                color: #fff;
            }
        }
    }
    &-highlight {
        transition: all 300ms ease-out;
        position: absolute;
        z-index: 9999;
        border: 1px solid #fff;
        border-radius: 3px;
        box-shadow: 0px 0px 0 10000px rgba(27, 28, 65, 0.5);
    }
    &-counter {
        float: left;
        color: #a4a4b2;
        text-align: middle;
        line-height: 200%;
    }
}
</style>
